import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import * as XLSX from 'xlsx'

const fmt = (n) => n ? Number(n).toLocaleString('en-US') : '—'

const XIcon     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const CheckIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4"><polyline points="20 6 9 17 4 12"/></svg>
const PlusIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const TrashIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round" strokeLinejoin="round"/></svg>
const EditIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
const UploadIcon= () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/></svg>
const DownloadIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/></svg>

const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium
      ${type === 'success' ? 'bg-[#1A7A4A] text-white' : 'bg-red-600 text-white'}`}>
      {type === 'success' ? <CheckIcon/> : <XIcon/>}
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><XIcon/></button>
    </div>
  )
}

// ── Confirm Overlay ──────────────────────────────────────────
const ConfirmOverlay = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onCancel}/>
    <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80">
      <p className="text-sm text-[#1C1A18] mb-5">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
      </div>
    </div>
  </div>
)

// ── Material Modal ───────────────────────────────────────────
const SYSTEM_DEFAULT_CRITICAL_PCT = 10
const SYSTEM_DEFAULT_REORDER_PCT = 20

const MaterialModal = ({ material, onSave, onClose }) => {
  const hasCustomPolicy = material?.threshold_type != null
  const [form, setForm] = useState({
    material_code: material?.material_code || '',
    description:   material?.description   || '',
    unit:          material?.unit          || '',
    category:      material?.category      || '',
  })
  // Stock Policy state kept separate: null/null/null means "Default" (nothing stored),
  // matching the DB — we never write 10/20 literally unless the user customizes.
  const [policyMode, setPolicyMode] = useState(hasCustomPolicy ? 'customized' : 'default')
  const [thresholdType, setThresholdType] = useState(material?.threshold_type || 'percentage')
  const [criticalLevel, setCriticalLevel] = useState(material?.critical_level ?? '')
  const [reorderLevel, setReorderLevel] = useState(material?.reorder_level ?? '')

  const h = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Validation mirrors the DB CHECK constraints exactly, so the user sees the
  // same rule here instead of discovering it only after a failed save.
  const policyError = (() => {
    if (policyMode !== 'customized') return null
    const c = parseFloat(criticalLevel)
    const r = parseFloat(reorderLevel)
    if (criticalLevel === '' || reorderLevel === '') return 'Critical and Reorder levels are required when Customized.'
    if (isNaN(c) || isNaN(r)) return 'Critical and Reorder levels must be numbers.'
    if (thresholdType === 'percentage' && (c < 0 || c > 100 || r < 0 || r > 100)) return 'Percentage values must be between 0 and 100.'
    if (thresholdType === 'quantity' && (c < 0 || r < 0)) return 'Quantity values cannot be negative.'
    if (c >= r) return 'Critical Level must be less than Reorder Level.'
    return null
  })()

  const handleSave = () => {
    if (policyError) return
    const policy = policyMode === 'default'
      ? { threshold_type: null, critical_level: null, reorder_level: null }
      : { threshold_type: thresholdType, critical_level: parseFloat(criticalLevel), reorder_level: parseFloat(reorderLevel) }
    onSave({ ...form, ...policy })
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-[#1C1A18] mb-5">
          {material ? 'Edit Material' : 'Add Material'}
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Material Code *</label>
              <input
                value={form.material_code}
                onChange={e => h('material_code', e.target.value.toUpperCase())}
                placeholder="e.g. STL-12MM"
                className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 uppercase"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Unit</label>
              <input
                value={form.unit}
                onChange={e => h('unit', e.target.value)}
                placeholder="e.g. MT, PC, M2"
                className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Description *</label>
            <input
              value={form.description}
              onChange={e => h('description', e.target.value)}
              placeholder="e.g. Steel Rebar 12mm"
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Category</label>
            <input
              value={form.category}
              onChange={e => h('category', e.target.value)}
              placeholder="e.g. Steel, Concrete, Electrical"
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"
            />
          </div>

          {/* Stock Policy section */}
          <div className="border-t border-[#E5E7EB] pt-4">
            <label className="block text-xs font-semibold text-[#6B7280] mb-2 uppercase tracking-wide">Stock Policy</label>
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => setPolicyMode('default')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border ${policyMode==='default' ? 'bg-[#1B3A6B] text-white border-[#1B3A6B]' : 'border-[#E5E7EB] text-[#6B7280]'}`}>
                Default
              </button>
              <button type="button" onClick={() => setPolicyMode('customized')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border ${policyMode==='customized' ? 'bg-[#1B3A6B] text-white border-[#1B3A6B]' : 'border-[#E5E7EB] text-[#6B7280]'}`}>
                Customized
              </button>
            </div>

            {policyMode === 'default' ? (
              <p className="text-xs text-[#9CA3AF] bg-[#F9FAFB] rounded-lg px-3 py-2">
                Using system default: Critical {SYSTEM_DEFAULT_CRITICAL_PCT}% / Reorder {SYSTEM_DEFAULT_REORDER_PCT}% of Received Qty.
                No value is stored for this material until you customize it.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-4">
                  <label className="flex items-center gap-1.5 text-sm text-[#2E2C29]">
                    <input type="radio" checked={thresholdType==='percentage'} onChange={()=>setThresholdType('percentage')}/>
                    Percentage
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-[#2E2C29]">
                    <input type="radio" checked={thresholdType==='quantity'} onChange={()=>setThresholdType('quantity')}/>
                    Quantity ({form.unit || 'unit'})
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">
                      Critical Level {thresholdType==='percentage' ? '(%)' : `(${form.unit || 'qty'})`}
                    </label>
                    <input type="number" value={criticalLevel} onChange={e=>setCriticalLevel(e.target.value)}
                      placeholder={thresholdType==='percentage' ? 'e.g. 10' : 'e.g. 50'}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">
                      Reorder Level {thresholdType==='percentage' ? '(%)' : `(${form.unit || 'qty'})`}
                    </label>
                    <input type="number" value={reorderLevel} onChange={e=>setReorderLevel(e.target.value)}
                      placeholder={thresholdType==='percentage' ? 'e.g. 20' : 'e.g. 150'}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"/>
                  </div>
                </div>
                {policyError && <p className="text-xs text-red-600">{policyError}</p>}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!!policyError}
            className="px-4 py-2 text-sm bg-[#1B3A6B] text-white rounded-lg hover:bg-[#1B3A6B]/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {material ? 'Update' : 'Add Material'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Component ───────────────────────────────────────────
export default function MaterialMaster({ initProject }) {
  const [projects, setProjects]           = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  // Real authorization source -- can() mirrors has_permission() exactly.
  const { can } = useProjectAuth(selectedProject?.id)
  const [materials, setMaterials]         = useState([])
  const [loading, setLoading]             = useState(false)
  const [toast, setToast]                 = useState(null)
  const [search, setSearch]               = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [showModal, setShowModal]         = useState(false)
  const [editItem, setEditItem]           = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [uploadPreview, setUploadPreview] = useState(null)
  const [uploading, setUploading]         = useState(false)
  const [selected, setSelected]           = useState(new Set())

  const showToast = (message, type = 'success') => setToast({ message, type })

  useEffect(() => {
    supabase.from('projects').select('id, name').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (initProject) setSelectedProject(initProject)
  }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) loadMaterials(selectedProject.id)
  }, [selectedProject?.id]) // eslint-disable-line

  const loadMaterials = useCallback(async (projectId) => {
    setLoading(true)
    const { data } = await supabase
      .from('materials')
      .select('*')
      .eq('project_id', projectId)
      .order('material_code')
    setMaterials(data || [])
    setLoading(false)
  }, [])

  // ── Save (Add / Edit) ────────────────────────────────────
  const handleSave = async (form) => {
    if (!form.material_code.trim()) return showToast('Material Code مطلوب', 'error')
    if (!form.description.trim())   return showToast('Description مطلوب', 'error')

    const payload = {
      project_id:      selectedProject.id,
      material_code:   form.material_code.trim().toUpperCase(),
      description:      form.description.trim(),
      unit:            form.unit.trim() || null,
      category:        form.category.trim() || null,
      // Stock Policy: null/null/null means "Default" (nothing customized).
      // These come pre-validated from MaterialModal's own policyError check,
      // but the DB CHECK constraints (added in the Stock Policy migration)
      // are the ultimate source of truth and are re-checked here too.
      threshold_type:  form.threshold_type ?? null,
      critical_level:  form.critical_level ?? null,
      reorder_level:   form.reorder_level ?? null,
    }

    let error
    if (editItem) {
      ;({ error } = await supabase.from('materials').update(payload).eq('id', editItem.id))
    } else {
      ;({ error } = await supabase.from('materials').insert(payload))
    }

    if (error) {
      if (error.code === '23505') return showToast('الكود موجود بالفعل', 'error')
      if (error.code === '23514') return showToast('Stock Policy غير صالحة: Critical لازم يكون أقل من Reorder، والقيم في النطاق الصحيح', 'error')
      return showToast(error.message, 'error')
    }

    showToast(editItem ? 'تم التحديث' : 'تمت الإضافة')
    setShowModal(false)
    setEditItem(null)
    loadMaterials(selectedProject.id)
  }

  // ── Delete ───────────────────────────────────────────────
  const handleDelete = async (id) => {
    const { error } = await supabase.from('materials').delete().eq('id', id)
    if (error) return showToast('فشل الحذف: ' + error.message, 'error')
    showToast('تم الحذف')
    setConfirmDelete(null)
    loadMaterials(selectedProject.id)
  }

  // ── Bulk Delete ──────────────────────────────────────────
  const handleBulkDelete = async () => {
    const ids = [...selected]
    const { error } = await supabase.from('materials').delete().in('id', ids)
    if (error) return showToast('فشل الحذف', 'error')
    showToast(`تم حذف ${ids.length} مادة`)
    setSelected(new Set())
    loadMaterials(selectedProject.id)
  }

  // ── Excel Upload ─────────────────────────────────────────
  const handleFileSelect = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
      const preview = rows
        .filter(r => r.material_code || r['Material Code'] || r['MATERIAL CODE'])
        .map(r => ({
          material_code: String(r.material_code || r['Material Code'] || r['MATERIAL CODE'] || '').trim().toUpperCase(),
          description:   String(r.description   || r['Description']   || r['DESCRIPTION']   || '').trim(),
          unit:          String(r.unit           || r['Unit']          || r['UNIT']          || '').trim(),
          category:      String(r.category       || r['Category']      || r['CATEGORY']      || '').trim(),
        }))
        .filter(r => r.material_code && r.description)
      setUploadPreview(preview)
    }
    reader.readAsArrayBuffer(file)
  }

  const handleConfirmUpload = async () => {
    if (!uploadPreview?.length) return
    setUploading(true)
    const payload = uploadPreview.map(r => ({
      project_id:    selectedProject.id,
      material_code: r.material_code,
      description:   r.description,
      unit:          r.unit || null,
      category:      r.category || null,
    }))

    // Upsert by project_id + material_code
    const { error } = await supabase.from('materials').upsert(payload, { onConflict: 'project_id,material_code' })
    setUploading(false)
    if (error) return showToast('فشل الرفع: ' + error.message, 'error')
    showToast(`تم رفع ${payload.length} مادة`)
    setUploadPreview(null)
    loadMaterials(selectedProject.id)
  }

  // ── Download Template ────────────────────────────────────
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['material_code', 'description', 'unit', 'category'],
      ['STL-12MM', 'Steel Rebar 12mm', 'MT', 'Steel'],
      ['STL-16MM', 'Steel Rebar 16mm', 'MT', 'Steel'],
      ['CEM-OPC',  'OPC Cement 50kg',  'BAG', 'Concrete'],
      ['PVC-110',  'PVC Pipe 110mm',   'ML', 'Plumbing'],
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Materials')
    XLSX.writeFile(wb, 'material_master_template.xlsx')
  }

  // ── Export ───────────────────────────────────────────────
  const exportData = () => {
    if (!materials.length) return showToast('مفيش بيانات', 'error')
    const rows = materials.map(m => ({
      material_code: m.material_code,
      description:   m.description,
      unit:          m.unit || '',
      category:      m.category || '',
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Materials')
    XLSX.writeFile(wb, `material_master_${selectedProject?.name || 'export'}.xlsx`)
  }

  // ── Filter ───────────────────────────────────────────────
  const categories = [...new Set(materials.map(m => m.category).filter(Boolean))].sort()

  const filtered = materials.filter(m => {
    const matchSearch = !search ||
      m.material_code.toLowerCase().includes(search.toLowerCase()) ||
      m.description.toLowerCase().includes(search.toLowerCase())
    const matchCat = categoryFilter === 'all' || m.category === categoryFilter
    return matchSearch && matchCat
  })

  const toggleSelect = (id) => {
    setSelected(s => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(m => m.id)))
  }

  // ── No project selected ──────────────────────────────────
  if (!selectedProject) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-[#1C1A18]">Material Master</h1>
          <p className="text-sm text-[#6B7280] mt-1">Manage your project material catalog</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 text-center">
          <p className="text-sm text-[#6B7280] mb-4">اختر مشروع أولاً</p>
          <select
            onChange={e => {
              const p = projects.find(x => x.id === e.target.value)
              if (p) setSelectedProject(p)
            }}
            className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"
          >
            <option value="">-- اختر مشروع --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-5">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)}/>}
      {confirmDelete && (
        <ConfirmOverlay
          message={`حذف "${confirmDelete.material_code}"؟`}
          onConfirm={() => handleDelete(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {showModal && (
        <MaterialModal
          material={editItem}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditItem(null) }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1C1A18]">Material Master</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{selectedProject.name} · {fmt(materials.length)} مادة</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadTemplate} className="flex items-center gap-1.5 px-3 py-2 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">
            <DownloadIcon/> Template
          </button>
          <button onClick={exportData} className="flex items-center gap-1.5 px-3 py-2 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">
            <DownloadIcon/> Export
          </button>
          {can('Material.Create') && (
            <label className="flex items-center gap-1.5 px-3 py-2 text-xs border border-[#1B3A6B] text-[#1B3A6B] rounded-lg hover:bg-[#1B3A6B]/5 cursor-pointer">
              <UploadIcon/> Upload Excel
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => { if (e.target.files[0]) handleFileSelect(e.target.files[0]) }}/>
            </label>
          )}
          {can('Material.Create') && (
            <button
              onClick={() => { setEditItem(null); setShowModal(true) }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#1B3A6B] text-white rounded-lg hover:bg-[#1B3A6B]/90"
            >
              <PlusIcon/> Add Material
            </button>
          )}
        </div>
      </div>

      {/* Upload Preview */}
      {uploadPreview && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-[#1C1A18]">معاينة — {uploadPreview.length} مادة</span>
            <div className="flex gap-2">
              <button onClick={() => setUploadPreview(null)} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
              <button onClick={handleConfirmUpload} disabled={uploading} className="px-3 py-1.5 text-xs bg-[#1B3A6B] text-white rounded-lg hover:bg-[#1B3A6B]/90 disabled:opacity-60">
                {uploading ? 'Uploading...' : '✓ Confirm Upload'}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-60 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#1B3A6B] text-white sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-left">Category</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {uploadPreview.slice(0, 50).map((r, i) => (
                  <tr key={i} className="hover:bg-[#F9FAFB]">
                    <td className="px-3 py-1.5 font-mono font-medium text-[#1B3A6B]">{r.material_code}</td>
                    <td className="px-3 py-1.5 text-[#1C1A18]">{r.description}</td>
                    <td className="px-3 py-1.5 text-[#6B7280]">{r.unit || '—'}</td>
                    <td className="px-3 py-1.5 text-[#6B7280]">{r.category || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Materials', value: materials.length, color: 'text-[#1C1A18]' },
          { label: 'Categories',      value: categories.length, color: 'text-[#1B3A6B]' },
          { label: 'With Unit',       value: materials.filter(m => m.unit).length, color: 'text-[#1A7A4A]' },
          { label: 'No Category',     value: materials.filter(m => !m.category).length, color: 'text-amber-600' },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <p className="text-xs text-[#9CA3AF] uppercase font-semibold mb-1">{k.label}</p>
            <p className={`text-2xl font-bold font-mono ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search code or description..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 text-[#6B7280]"
        >
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {selected.size > 0 && can('Material.Delete') && (
          <button onClick={handleBulkDelete} className="flex items-center gap-1.5 px-3 py-2 text-xs bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100">
            <TrashIcon/> Delete {selected.size}
          </button>
        )}
        <span className="text-xs text-[#9CA3AF] ml-auto">{filtered.length} مادة</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#9CA3AF]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-[#9CA3AF] mb-3">
              {materials.length === 0 ? 'مفيش مواد — ارفع Excel أو أضف يدوي' : 'مفيش نتائج'}
            </p>
            {materials.length === 0 && can('Material.Create') && (
              <button
                onClick={() => { setEditItem(null); setShowModal(true) }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#1B3A6B] text-white rounded-lg hover:bg-[#1B3A6B]/90"
              >
                <PlusIcon/> Add First Material
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Material Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Stock Policy</th>
                <th className="px-4 py-3 w-20"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {filtered.map(m => (
                <tr key={m.id} className={`hover:bg-[#F9FAFB] ${selected.has(m.id) ? 'bg-[#EEF2FF]' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggleSelect(m.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-[#1B3A6B] text-xs bg-[#EEF2FF] px-2 py-0.5 rounded">
                      {m.material_code}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#1C1A18]">{m.description}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{m.unit || '—'}</td>
                  <td className="px-4 py-3">
                    {m.category ? (
                      <span className="text-xs bg-[#F3F4F6] text-[#6B7280] px-2 py-0.5 rounded-full">{m.category}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {m.threshold_type == null ? (
                      <span className="text-xs bg-[#F3F4F6] text-[#9CA3AF] px-2 py-0.5 rounded-full">Default ({SYSTEM_DEFAULT_CRITICAL_PCT}%/{SYSTEM_DEFAULT_REORDER_PCT}%)</span>
                    ) : (
                      <span className="text-xs bg-[#EEF2FF] text-[#1B3A6B] px-2 py-0.5 rounded-full font-medium">
                        Customized ({m.critical_level}{m.threshold_type==='percentage'?'%':` ${m.unit||''}`} / {m.reorder_level}{m.threshold_type==='percentage'?'%':` ${m.unit||''}`})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {can('Material.Edit') && (
                        <button
                          onClick={() => { setEditItem(m); setShowModal(true) }}
                          className="p-1.5 text-[#6B7280] hover:text-[#1B3A6B] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                        >
                          <EditIcon/>
                        </button>
                      )}
                      {can('Material.Delete') && (
                        <button
                          onClick={() => setConfirmDelete(m)}
                          className="p-1.5 text-[#6B7280] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
